import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/client';
import toast from 'react-hot-toast';
import {
  Shield, Download, Trash2, Eye, Lightbulb, ArrowLeft,
  ChevronDown, ChevronUp, CheckCircle, XCircle, AlertTriangle,
  UserCheck, Stethoscope, FileText, Lock, Users, Plus, X, Mail
} from 'lucide-react';

const Toggle = ({ checked, onChange, disabled }) => (
  <button
    onClick={() => !disabled && onChange(!checked)}
    disabled={disabled}
    className={`relative w-11 h-6 rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary/30 ${
      checked ? 'bg-primary' : 'bg-slate-300'
    } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
  >
    <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform duration-200 ${checked ? 'translate-x-5' : 'translate-x-0'}`} />
  </button>
);

const MyDataPrivacy = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [suggestions, setSuggestions] = useState([]);
  const [consent, setConsent] = useState(null);
  const [auditTrail, setAuditTrail] = useState(null);
  const [showAudit, setShowAudit] = useState(false);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditError, setAuditError] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [updatingConsent, setUpdatingConsent] = useState(false);
  const [caregivers, setCaregivers] = useState([]);
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [inviteForm, setInviteForm] = useState({ caregiverName: '', caregiverEmail: '', relationship: 'other' });
  const [inviting, setInviting] = useState(false);
  const [revoking, setRevoking] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const [sugRes, consentRes] = await Promise.all([
          api.get('/transparency/improvement-suggestions').catch(() => null),
          api.get('/transparency/consent-status').catch(() => null),
        ]);
        if (sugRes?.data?.data?.suggestions) setSuggestions(sugRes.data.data.suggestions);
        if (consentRes?.data?.data) setConsent(consentRes.data.data);
        const cgRes = await api.get('/caregiver/my-caregivers').catch(() => null);
        if (cgRes?.data?.data) setCaregivers(cgRes.data.data);
      } catch {
        // defaults
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const loadAuditTrail = async () => {
    if (auditTrail) {
      setShowAudit(prev => !prev);
      return;
    }
    setShowAudit(true);
    setAuditLoading(true);
    setAuditError(null);
    try {
      const res = await api.get('/transparency/my-data');
      if (res?.data?.data) {
        setAuditTrail(res.data.data);
      } else {
        setAuditError('No data returned from server.');
      }
    } catch (err) {
      console.error('Audit trail fetch error:', err);
      const msg = err?.response?.data?.detail || err?.response?.data?.error || 'Failed to load audit trail. Please try again.';
      setAuditError(msg);
    } finally {
      setAuditLoading(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await api.post('/transparency/export-my-data', { format: 'json' });
      const blob = new Blob([JSON.stringify(res.data.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `my-health-data-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Health data exported');
    } catch {
      toast.error('Failed to export data');
    } finally {
      setExporting(false);
    }
  };

  const handleDeleteRequest = async () => {
    if (!window.confirm('Are you sure you want to request deletion of all your data? This action cannot be undone.')) return;
    setDeleting(true);
    try {
      await api.post('/transparency/request-data-deletion', { reason: 'User requested via My Data & Privacy page', confirmDelete: true });
      toast.success('Data deletion request submitted. An admin will review it.');
    } catch {
      toast.error('Failed to submit deletion request');
    } finally {
      setDeleting(false);
    }
  };

  const handleInviteCaregiver = async (e) => {
    e.preventDefault();
    if (!inviteForm.caregiverEmail || !inviteForm.caregiverName) { toast.error('Name and email required'); return; }
    setInviting(true);
    try {
      const res = await api.post('/caregiver/invite', inviteForm);
      setCaregivers(prev => [res.data.data, ...prev]);
      setInviteForm({ caregiverName: '', caregiverEmail: '', relationship: 'other' });
      setShowInviteForm(false);
      toast.success('Invite sent — they\'ll receive an email with a link to create their account.');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to send invite');
    } finally {
      setInviting(false);
    }
  };

  const handleResendInvite = async (id) => {
    try {
      await api.post(`/caregiver/${id}/resend`);
      toast.success('Invite resent — new link valid for 7 days');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to resend invite');
    }
  };

  const handleRevokeCaregiver = async (id) => {
    if (!window.confirm('Remove this caregiver\'s access?')) return;
    setRevoking(id);
    try {
      await api.delete(`/caregiver/${id}`);
      setCaregivers(prev => prev.filter(c => c._id !== id));
      toast.success('Access revoked');
    } catch {
      toast.error('Failed to revoke access');
    } finally {
      setRevoking(null);
    }
  };

  const handleConsentToggle = async (field, value) => {
    setUpdatingConsent(true);
    try {
      await api.put('/transparency/update-consent', { [field]: value });
      setConsent(prev => ({ ...prev, [field]: value }));
      toast.success('Consent preference updated');
    } catch {
      toast.error('Failed to update consent');
    } finally {
      setUpdatingConsent(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4 py-6">
        {[1, 2, 3].map(i => <div key={i} className="h-24 rounded-2xl animate-pulse bg-slate-100" />)}
      </div>
    );
  }

  return (
    <div className="space-y-6 py-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        
        <div>
          <h2 className="font-headline text-2xl font-extrabold text-slate-900 tracking-tight">My Data & Privacy</h2>
          <p className="text-slate-500 text-sm mt-1">Manage your data, consent, and privacy preferences</p>
        </div>
      </div>

      {/* Consent Preferences */}
      {consent && (
        <div className="bg-white rounded-2xl shadow-sm border border-outline-variant/10 overflow-hidden">
          <div className="p-5 border-b border-slate-100">
            <h3 className="font-bold text-lg text-slate-900 flex items-center gap-2">
              <Shield className="w-5 h-5 text-primary" /> Consent Preferences
            </h3>
            <p className="text-xs text-slate-500 mt-1">Control how your data is used</p>
          </div>
          <div className="divide-y divide-slate-100">
            {[
              { field: 'dataSharingConsent', label: 'Default Care Team Access', desc: 'Allow new care team doctors to access your health data without explicit permission' },
              { field: 'researchConsent', label: 'Research Participation', desc: 'Allow anonymized data use for medical research' },
              { field: 'emergencyContactConsent', label: 'Emergency Notifications', desc: 'Allow automatic notification of your emergency contact during critical health events' },
            ].map(item => (
              <div key={item.field} className="flex items-center justify-between p-5">
                <div>
                  <p className="text-sm font-bold text-slate-900">{item.label}</p>
                  <p className="text-xs text-slate-500">{item.desc}</p>
                </div>
                <Toggle
                  checked={consent[item.field] !== false}
                  onChange={(val) => handleConsentToggle(item.field, val)}
                  disabled={updatingConsent}
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Health Improvement Suggestions */}
      {suggestions.length > 0 && (
        <div className="bg-white rounded-2xl shadow-sm border border-outline-variant/10 overflow-hidden">
          <div className="p-5 border-b border-slate-100">
            <h3 className="font-bold text-lg text-slate-900 flex items-center gap-2">
              <Lightbulb className="w-5 h-5 text-amber-500" /> Personalized Suggestions
            </h3>
            <p className="text-xs text-slate-500 mt-1">Based on your health profile and risk assessment</p>
          </div>
          <div className="divide-y divide-slate-100">
            {suggestions.map((s, i) => (
              <div key={i} className="p-5">
                <p className="text-sm font-bold text-slate-900">{s.area}</p>
                <p className="text-sm text-slate-600 mt-1">{s.action}</p>
                {s.impact && <p className="text-xs text-primary font-medium mt-2">Impact: {s.impact}</p>}
                {s.how && <p className="text-xs text-slate-500 mt-1">{s.how}</p>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Audit Trail */}
      <div className="bg-white rounded-2xl shadow-sm border border-outline-variant/10 overflow-hidden">
        <button onClick={loadAuditTrail} className="w-full p-5 flex items-center justify-between text-left hover:bg-slate-50 transition-all">
          <div className="flex items-center gap-3">
            <Eye className="w-5 h-5 text-primary" />
            <div>
              <p className="text-sm font-bold text-slate-900">Data Access Audit Trail</p>
              <p className="text-xs text-slate-500">See who accessed your data and when</p>
            </div>
          </div>
          {showAudit ? <ChevronUp className="w-5 h-5 text-slate-500" /> : <ChevronDown className="w-5 h-5 text-slate-500" />}
        </button>
        {showAudit && (
          <div className="border-t border-slate-100 p-5 max-h-80 overflow-y-auto">
            {auditLoading && (
              <div className="flex items-center justify-center gap-2 py-6 text-slate-400">
                <div className="w-4 h-4 rounded-full border-2 border-primary border-t-transparent animate-spin" />
                <span className="text-xs">Loading audit trail…</span>
              </div>
            )}
            {auditError && !auditLoading && (
              <div className="flex items-center gap-2 p-3 bg-red-50 rounded-xl text-xs text-red-600">
                <AlertTriangle className="w-4 h-4 shrink-0" />
                {auditError}
              </div>
            )}
            {auditTrail && !auditLoading && (
              <div className="space-y-5">
                {/* Summary row */}
                {auditTrail.summary && (
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {[
                      { label: 'Data Access Events', value: auditTrail.summary.totalAccessEvents ?? 0, icon: Eye, color: 'text-primary' },
                      { label: 'Unique Accessors', value: auditTrail.summary.uniqueAccessors ?? 0, icon: UserCheck, color: 'text-emerald-600' },
                      { label: 'Health Events', value: auditTrail.summary.totalEvents ?? 0, icon: FileText, color: 'text-amber-600' },
                      { label: 'Risk Assessments', value: auditTrail.summary.totalAssessments ?? 0, icon: Shield, color: 'text-violet-600' },
                    ].map((stat, i) => {
                      const Icon = stat.icon;
                      return (
                        <div key={i} className="bg-slate-50 rounded-xl p-3 text-center">
                          <Icon className={`w-4 h-4 ${stat.color} mx-auto mb-1`} />
                          <p className="text-lg font-extrabold text-slate-900">{stat.value}</p>
                          <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wide leading-tight">{stat.label}</p>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Who accessed your data */}
                <div>
                  <p className="text-xs font-extrabold text-slate-500 uppercase tracking-widest mb-2 flex items-center gap-1.5">
                    <Lock className="w-3.5 h-3.5" /> Who accessed your data
                  </p>
                  {(auditTrail.accessLogs || []).length === 0 ? (
                    <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-4 flex items-center gap-3">
                      <CheckCircle className="w-4 h-4 text-emerald-600 shrink-0" />
                      <p className="text-xs text-emerald-700 font-medium">No access events recorded in the last 90 days.</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {(auditTrail.accessLogs || []).map((log, i) => {
                        const actionLabel = {
                          PHI_ACCESS: 'Viewed',
                          PHI_CREATE: 'Created',
                          PHI_UPDATE: 'Updated',
                          PHI_DELETE: 'Deleted',
                          EXPORT: 'Exported',
                        }[log.eventType] || log.eventType;

                        const resourceLabel = {
                          Symptom: 'symptom report',
                          PatientProfile: 'health profile',
                          RiskAssessment: 'risk assessment',
                          CareEvent: 'care event',
                        }[log.resourceType] || (log.resourceType || 'data').toLowerCase();

                        return (
                          <div key={i} className="flex items-start gap-3 p-3 rounded-xl bg-slate-50 hover:bg-slate-100 transition-colors">
                            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
                              <Stethoscope className="w-4 h-4 text-primary" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-bold text-slate-900">
                                {log.accessedBy.name}
                                {log.accessedBy.specialization && (
                                  <span className="font-normal text-slate-500"> · {log.accessedBy.specialization}</span>
                                )}
                              </p>
                              <p className="text-xs text-slate-600 mt-0.5">
                                {actionLabel} your {resourceLabel}
                                {log.emergencyAccess && (
                                  <span className="ml-1.5 px-1.5 py-0.5 bg-red-100 text-red-600 rounded text-[10px] font-bold">Emergency Access</span>
                                )}
                              </p>
                              <p className="text-[11px] text-slate-400 mt-0.5">
                                {new Date(log.timestamp).toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                              </p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Health activity timeline (CareEvents) */}
                {(auditTrail.events || []).length > 0 && (
                  <div>
                    <p className="text-xs font-extrabold text-slate-500 uppercase tracking-widest mb-2 flex items-center gap-1.5">
                      <FileText className="w-3.5 h-3.5" /> Health activity timeline
                    </p>
                    <div className="space-y-1.5">
                      {(auditTrail.events || []).slice(0, 15).map((evt, i) => (
                        <div key={i} className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-slate-50">
                          <div className={`w-2 h-2 rounded-full shrink-0 ${evt.severity === 'high' || evt.severity === 'critical' ? 'bg-red-400' : evt.severity === 'medium' ? 'bg-amber-400' : 'bg-slate-300'}`} />
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-medium text-slate-700 truncate">{evt.eventType || evt.type}</p>
                            <p className="text-[11px] text-slate-400">{evt.timestamp ? new Date(evt.timestamp).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Per-Doctor Consent Management */}
      <button
        onClick={() => navigate('/data-consent')}
        className="w-full bg-white rounded-2xl p-5 shadow-sm border border-outline-variant/10 flex items-center justify-between hover:shadow-md transition-all text-left"
      >
        <div className="flex items-center gap-4">
          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
            <Shield className="w-5 h-5 text-primary" />
          </div>
          <div>
            <p className="text-sm font-bold text-slate-900">Doctor Data Access</p>
            <p className="text-xs text-slate-500">Manage which doctors can see your data and what they can access</p>
          </div>
        </div>
        <ChevronDown className="w-5 h-5 text-slate-300 -rotate-90" />
      </button>

      {/* Caregiver Access */}
      <div className="bg-white rounded-2xl shadow-sm border border-outline-variant/10 overflow-hidden">
        <div className="p-5 border-b border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Users className="w-5 h-5 text-primary" />
            <div>
              <p className="text-sm font-bold text-slate-900">Family & Caregivers</p>
              <p className="text-xs text-slate-500">Let a family member see your general health status</p>
            </div>
          </div>
          <button onClick={() => setShowInviteForm(p => !p)}
            className="flex items-center gap-1 text-xs font-bold text-primary hover:bg-primary/10 px-2.5 py-1.5 rounded-lg transition-all">
            {showInviteForm ? <X className="w-3.5 h-3.5" /> : <Plus className="w-3.5 h-3.5" />}
            {showInviteForm ? 'Cancel' : 'Add'}
          </button>
        </div>

        {showInviteForm && (
          <form onSubmit={handleInviteCaregiver} className="p-5 border-b border-slate-100 space-y-3 bg-slate-50">
            <div className="grid grid-cols-2 gap-2">
              <input required placeholder="Their name" value={inviteForm.caregiverName}
                onChange={e => setInviteForm(p => ({ ...p, caregiverName: e.target.value }))}
                className="px-3 py-2 text-sm bg-white border border-slate-200 rounded-lg" />
              <select value={inviteForm.relationship}
                onChange={e => setInviteForm(p => ({ ...p, relationship: e.target.value }))}
                className="px-3 py-2 text-sm bg-white border border-slate-200 rounded-lg">
                <option value="spouse">Spouse / Partner</option>
                <option value="parent">Parent</option>
                <option value="child">Child</option>
                <option value="sibling">Sibling</option>
                <option value="friend">Friend</option>
                <option value="other">Other</option>
              </select>
            </div>
            <input required type="email" placeholder="Their email address" value={inviteForm.caregiverEmail}
              onChange={e => setInviteForm(p => ({ ...p, caregiverEmail: e.target.value }))}
              className="w-full px-3 py-2 text-sm bg-white border border-slate-200 rounded-lg" />
            <button type="submit" disabled={inviting}
              className="w-full py-2 bg-primary text-white text-sm font-bold rounded-lg hover:bg-primary/90 transition-all disabled:opacity-50 flex items-center justify-center gap-2">
              <Mail className="w-3.5 h-3.5" />
              {inviting ? 'Sending…' : 'Send invite email'}
            </button>
            <p className="text-[11px] text-slate-400">They'll see last check-in time and general status only — no clinical data.</p>
          </form>
        )}

        {caregivers.length === 0 ? (
          <div className="p-5 text-center">
            <p className="text-sm text-slate-400">No caregivers added yet</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {caregivers.map(cg => (
              <div key={cg._id} className="flex items-center justify-between p-4 gap-3">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-slate-900 truncate">{cg.caregiverName || cg.caregiverEmail}</p>
                  <p className="text-xs text-slate-500 truncate">{cg.caregiverEmail}</p>
                  <span className={`inline-block mt-1 text-[10px] font-bold px-2 py-0.5 rounded-full ${
                    cg.status === 'active' ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'
                  }`}>{cg.status === 'active' ? 'Active' : 'Invite pending'}</span>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {cg.status === 'pending' && (
                    <button onClick={() => handleResendInvite(cg._id)}
                      className="text-[10px] font-bold px-2.5 py-1.5 rounded-lg text-primary hover:bg-primary/10 transition-all">
                      Resend
                    </button>
                  )}
                  <button onClick={() => handleRevokeCaregiver(cg._id)} disabled={revoking === cg._id}
                    className="p-1.5 hover:bg-red-50 rounded-lg transition-all text-slate-300 hover:text-red-500">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Data Actions */}
      <div className="space-y-3">
        <button
          onClick={handleExport}
          disabled={exporting}
          className="w-full flex items-center justify-center gap-2 px-5 py-3 bg-primary text-white rounded-2xl font-bold text-sm shadow-md hover:shadow-lg active:scale-95 transition-all disabled:opacity-50"
        >
          <Download className="w-4 h-4" />
          {exporting ? 'Exporting...' : 'Download All My Data'}
        </button>
        <button
          onClick={handleDeleteRequest}
          disabled={deleting}
          className="w-full flex items-center justify-center gap-2 px-5 py-3 bg-red-50 text-red-600 rounded-2xl font-bold text-sm hover:bg-red-100 active:scale-95 transition-all disabled:opacity-50"
        >
          <Trash2 className="w-4 h-4" />
          {deleting ? 'Submitting...' : 'Request Data Deletion'}
        </button>
      </div>

      <p className="text-center text-xs text-slate-500">
        Your data rights are protected under applicable healthcare privacy regulations.
      </p>
    </div>
  );
};

export default MyDataPrivacy;
